import { useState, useEffect, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { settingsApi, tenantsApi, WITHHOLDING_RULE_LABEL, type WithholdingRule } from "../api.js";
import { toast } from "../components/Toast.js";
import { Button } from "../components/ui/Button.js";
import { UserManagementCard } from "../components/settings/UserManagementCard.js";
import { useScreenView } from "../lib/analytics.js";

const inputStyle: CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  padding: "6px 10px",
  fontSize: 13,
  fontFamily: "inherit",
  color: "#0f172a",
};

const fieldLabelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "#64748b",
  marginBottom: 4,
};

/** マスタ管理（値を編集する画面）への導線 */
const MASTER_LINKS = [
  {
    to: "/settings/professions",
    label: "資格の種類",
    desc: "司法書士・行政書士・土地家屋調査士＋カスタム資格の種類と、資格者名簿・既定の資格者を設定します。",
  },
  {
    to: "/settings/work-items",
    label: "業務マスタ",
    desc: "請求書・見積書に足す定型業務と単価を登録・編集します。",
  },
  {
    to: "/settings/fee-schedules",
    label: "料金表",
    desc: "土地計算書・建物計算書・保証協会請求書の単価を編集します。",
  },
  {
    to: "/settings/templates",
    label: "テンプレート管理",
    desc: "PDF のレイアウト（帳票テンプレート）を編集します。",
  },
];

const masterLinkStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "12px 14px",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  textDecoration: "none",
  background: "#f8fafc",
};

export function SettingsPage() {
  useScreenView("settings");
  const qc = useQueryClient();
  const [rule, setRule] = useState<WithholdingRule>("corporation_only");
  const [bankInfo, setBankInfo] = useState("");
  const [transferNote, setTransferNote] = useState("");

  const [officeName, setOfficeName] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [address, setAddress] = useState("");
  const [tel, setTel] = useState("");
  const [fax, setFax] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: () => settingsApi.get(),
  });

  const { data: tenant, isLoading: isTenantLoading } = useQuery({
    queryKey: ["tenant", "me"],
    queryFn: () => tenantsApi.getMe(),
  });

  useEffect(() => {
    if (data) {
      setRule(data.withholding_rule);
      setBankInfo(data.bank_info ?? "");
      setTransferNote(data.transfer_note ?? "");
    }
  }, [data]);

  useEffect(() => {
    if (tenant) {
      setOfficeName(tenant.name ?? "");
      setPostalCode(tenant.postal_code ?? "");
      setAddress(tenant.address ?? "");
      setTel(tenant.tel ?? "");
      setFax(tenant.fax ?? "");
    }
  }, [tenant]);

  const settingsMut = useMutation({
    mutationFn: (successMessage: string) =>
      settingsApi.update({ withholding_rule: rule, bank_info: bankInfo, transfer_note: transferNote }).then(() => successMessage),
    onSuccess: (successMessage) => {
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast(successMessage);
    },
    onError: (e) => toast(e.message, "error"),
  });

  const officeMut = useMutation({
    mutationFn: () => {
      if (!officeName.trim()) throw new Error("事務所名は必須です");
      return tenantsApi.updateMe({
        name: officeName.trim(),
        postal_code: postalCode || null,
        address: address || null,
        tel: tel || null,
        fax: fax || null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", "me"] });
      toast("事務所情報を保存しました");
    },
    onError: (e) => toast(e.message, "error"),
  });

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 20, fontWeight: 800, marginBottom: 24 }}>テナント設定</h1>

      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, maxWidth: 560, marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px" }}>マスタ管理</h2>
        <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px" }}>
          業務名や単価など、書類作成時に使う値を事務所ごとに編集できます。
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {MASTER_LINKS.map((link) => (
            <Link key={link.to} to={link.to} style={masterLinkStyle}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{link.label}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{link.desc}</div>
              </div>
              <span style={{ color: "#0f766e", fontSize: 16, fontWeight: 800 }}>›</span>
            </Link>
          ))}
        </div>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, maxWidth: 560 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px" }}>事務所情報</h2>
        <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px" }}>
          PDFに印字される事務所情報を編集します。
        </p>

        {isTenantLoading ? (
          <p style={{ color: "#94a3b8" }}>読み込み中…</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={fieldLabelStyle}>事務所名</label>
              <input style={inputStyle} value={officeName} onChange={(e) => setOfficeName(e.target.value)} />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={fieldLabelStyle}>郵便番号</label>
              <input style={inputStyle} value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="444-0853" />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={fieldLabelStyle}>住所</label>
              <input style={inputStyle} value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={fieldLabelStyle}>TEL</label>
              <input style={inputStyle} value={tel} onChange={(e) => setTel(e.target.value)} />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={fieldLabelStyle}>FAX</label>
              <input style={inputStyle} value={fax} onChange={(e) => setFax(e.target.value)} />
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="primary"
            onClick={() => officeMut.mutate()}
            disabled={officeMut.isPending || isTenantLoading}
          >
            {officeMut.isPending ? "保存中…" : "事務所情報を保存"}
          </Button>
        </div>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, maxWidth: 560, marginTop: 20 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px" }}>書類の既定値</h2>
        <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px" }}>
          請求書・見積書に印字する振込先口座と振込案内文の既定値です。テンプレートから <code>{"{{office.bank_info}}"}</code> / <code>{"{{office.transfer_note}}"}</code> で参照できます。
        </p>

        {isLoading ? (
          <p style={{ color: "#94a3b8" }}>読み込み中…</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={fieldLabelStyle}>振込先口座</label>
              <textarea
                style={{ ...inputStyle, minHeight: 72, resize: "vertical" }}
                value={bankInfo}
                onChange={(e) => setBankInfo(e.target.value)}
                placeholder={"○○銀行 ○○支店\n普通 1234567\nナガサカアマノゴウドウジムショ"}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={fieldLabelStyle}>既定振込案内文</label>
              <input
                style={inputStyle}
                value={transferNote}
                onChange={(e) => setTransferNote(e.target.value)}
                placeholder="※振込手数料はお客様にてご負担願います。"
              />
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="primary"
            onClick={() => settingsMut.mutate("書類の既定値を保存しました")}
            disabled={settingsMut.isPending || isLoading}
          >
            {settingsMut.isPending ? "保存中…" : "既定値を保存"}
          </Button>
        </div>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, maxWidth: 560, marginTop: 20 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px" }}>源泉徴収税の適用ルール</h2>
        <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 20px" }}>
          どの顧客区分に源泉徴収を適用するか設定します。
          行政書士報酬には法律上適用されないため、本設定は司法書士・調査士報酬にのみ影響します。
        </p>

        {isLoading ? (
          <p style={{ color: "#94a3b8" }}>読み込み中…</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
            {(Object.entries(WITHHOLDING_RULE_LABEL) as [WithholdingRule, string][]).map(([value, label]) => (
              <label key={value} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="withholding_rule"
                  value={value}
                  checked={rule === value}
                  onChange={() => setRule(value)}
                  style={{ width: 16, height: 16, accentColor: "#0f766e" }}
                />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{label}</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>{WITHHOLDING_RULE_DESC[value]}</div>
                </div>
              </label>
            ))}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="primary"
            onClick={() => settingsMut.mutate("設定を保存しました")}
            disabled={settingsMut.isPending || isLoading}
          >
            {settingsMut.isPending ? "保存中…" : "設定を保存"}
          </Button>
        </div>
      </div>

      <UserManagementCard />
    </div>
  );
}

const WITHHOLDING_RULE_DESC: Record<WithholdingRule, string> = {
  corporation_only: "法人顧客の場合のみ源泉徴収を計算します（一般的な設定）",
  individual_only:  "個人顧客の場合のみ源泉徴収を計算します",
  all:              "顧客区分に関わらず常に源泉徴収を計算します",
  none:             "源泉徴収を一切計算しません",
};
